import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    viewChild,
} from '@angular/core';
import {
    LICENSE_UPGRADE_NOTE_MAX_LENGTH,
    licenseUpgradeDescriptionKey,
} from '@interacta-shared/data-access-license';
import {
    BreakpointService,
    DialogButtonComponent,
    DialogButtonsComponent,
    DialogComponent,
    Size,
} from '@interacta-shared/ui';
import { LicenseUpgradePanelComponent } from '@interacta-shared/ui-license';
import { TranslatePipe } from '@ngx-translate/core';
import { LicenseUpgradeDialogService } from '../../services/license-upgrade-dialog.service';
import { LicenseUpgradeSubmitService } from '../../services/license-upgrade-submit.service';

/**
 * Rendered once per app next to `interacta-confirm-dialog`: every blocked action
 * and the 403 fallback share this single instance through
 * `LicenseUpgradeDialogService`, which is also what collapses a burst of refused
 * calls into one dialog.
 */
@Component({
    selector: 'interacta-license-upgrade-dialog',
    templateUrl: './license-upgrade-dialog.component.html',
    imports: [
        DialogButtonComponent,
        DialogButtonsComponent,
        DialogComponent,
        LicenseUpgradePanelComponent,
        TranslatePipe,
    ],
    providers: [LicenseUpgradeSubmitService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LicenseUpgradeDialogComponent {
    private readonly dialogService = inject(LicenseUpgradeDialogService);
    private readonly breakpointService = inject(BreakpointService);

    readonly submitService = inject(LicenseUpgradeSubmitService);

    private readonly panel = viewChild(LicenseUpgradePanelComponent);
    private readonly target = this.dialogService.target;

    readonly isOpen = this.dialogService.isOpen;
    readonly noteMaxLength = LICENSE_UPGRADE_NOTE_MAX_LENGTH;

    readonly canRequestUpgrade = computed(() => !!this.target().operation);
    readonly descriptionKey = computed(() =>
        licenseUpgradeDescriptionKey(
            this.target().operation,
            !!this.target().communityName,
        ),
    );
    readonly translationVars = computed(() => ({
        communityName: this.target().communityName,
    }));

    /**
     * The note lives in the panel while the actions live in the dialog's own
     * button row, so the guard the panel applies to its own send has to be
     * readable from here too.
     */
    readonly canSend = computed(
        () =>
            !this.submitService.isSending() &&
            !(this.panel()?.noteInvalid() ?? false),
    );

    readonly dialogSize = computed<Size>(() => {
        const breakpoints = this.breakpointService.breakpointsSignal();
        if (breakpoints?.isXl) return 'regular';
        if (breakpoints?.isMd) return 'small';

        return 'extra-small';
    });

    submit(): void {
        this.panel()?.submit();
    }

    send(note: string): void {
        const { operation, context } = this.target();
        if (!operation) return;

        this.submitService.submit({ operation, note, context }, () =>
            this.close(),
        );
    }

    close(): void {
        this.dialogService.close();
        this.panel()?.reset();
    }
}
